// Pure ack-state helpers for the client-side ack reconciler.
//
// The reconciler compares two ack observations in ONE canonical, count-based
// representation so a monotonic diff is well-defined on every axis:
//
//   { ack, delivered, read }
//
//   - ack       : the wa-js scalar ack ladder (see ACK) — the send's coarse
//                 wire state. For groups the scalar pins at SERVER(1) until
//                 EVERY participant received, so it alone under-reports delivery.
//   - delivered : how many recipients reached ack >= DEVICE (2).
//   - read      : how many recipients reached ack >= READ  (3).
//
// For a 1:1 (DM) the counts collapse to 0/1. For a group they are real
// per-recipient counts. Both the WhatsApp store side (readStoreAckState) and
// the local Redux doc side (knownAckStateFromDoc) normalize into this shape so
// isMonotonicAdvance can be a trivial strict-greater on each axis.

import { ACK } from '@bt-common/schemas/wa/message-ack';

export interface AckState {
  /** wa-js scalar ack (-1..4). Never null here — a store miss is handled by
   *  the resolver (skip), not by this normalizer. */
  ack: number;
  /** Count of recipients at ack >= DEVICE. */
  delivered: number;
  /** Count of recipients at ack >= READ. */
  read: number;
}

/**
 * Structural input to {@link readStoreAckState}: any ack-carrying store row or
 * page-ack-bridge entry. Every field is optional and read defensively — this
 * accepts both a raw WhatsApp Store message (`{ ack, ackData }`) and the
 * bridge's materialized MsgInfo projection (`{ ack, delivery, read, played }` /
 * the `deliveredCount`/`readCount` slim aliases). A `WaMessageAckEntry` is
 * assignable here. Typed (not `any`) so a field rename fails the build. */
export interface AckSource {
  /** Scalar ack; `null`/absent both collapse to 0 (PENDING). */
  ack?: number | null;
  /** Per-recipient ack list (numbers or `{ ack }` entries) — group case. */
  ackData?: unknown;
  delivery?: unknown;
  read?: unknown;
  played?: unknown;
  deliveredCount?: unknown;
  readCount?: unknown;
  /** Present on the MsgInfo bridge payload; unused here but allowed so those
   *  literals type-check without an escape hatch. */
  deliveryRemaining?: unknown;
  readRemaining?: unknown;
  playedRemaining?: unknown;
}

/** Coerce an unknown ack-ish value to a finite number, defaulting to 0
 *  (PENDING). `null` (the bridge's "no ack yet" sentinel) and garbage both
 *  collapse to 0 — a non-advance against any real prior state. */
function toAck(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/** Read a numeric field defensively; undefined when absent/non-numeric. */
function toNum(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Extract a per-recipient ack list from a store-message-like object, when one
 *  is present. Recognizes `ackData` as an array of raw ack numbers OR of
 *  `{ ack }` entries (the spec's canonical group representation). Returns null
 *  when there is no per-recipient collection (DM, or a lean store row). */
function extractRecipientAcks(m: AckSource): number[] | null {
  const ad = m.ackData;
  if (!Array.isArray(ad) || ad.length === 0) return null;
  const acks: number[] = [];
  for (const entry of ad) {
    if (typeof entry === 'number') {
      acks.push(entry);
    } else if (entry && typeof entry === 'object' && 'ack' in entry) {
      const a = toNum((entry as { ack?: unknown }).ack);
      if (a != null) acks.push(a);
    }
  }
  return acks.length ? acks : null;
}

/** Extract already-materialized delivered/read COUNTS from a store-message-like
 *  object. Handles the WAWeb MsgInfo projection surfaced by the page ack bridge
 *  (`getWaMessagesAckDataByWaMessagesKeys`): `delivery`/`read`/`played` (or the
 *  `deliveredCount`/`readCount` slim aliases) are per-recipient counts. Returns
 *  null when no count fields are present. */
function extractCounts(m: AckSource): { delivered: number; read: number } | null {
  const delivered = toNum(m.deliveredCount) ?? toNum(m.delivery);
  const read = toNum(m.readCount) ?? toNum(m.read);
  if (delivered == null && read == null) return null;
  return { delivered: delivered ?? 0, read: read ?? 0 };
}

/**
 * Normalize a WhatsApp store message (or a store-derived ack payload from the
 * page ack bridge) into the canonical `{ ack, delivered, read }`.
 *
 * Precedence: an explicit per-recipient `ackData` array wins; then materialized
 * MsgInfo counts; otherwise (DM, or missing per-recipient data) we DERIVE the
 * counts from the scalar ack — the defensive fallback the spec calls for.
 *
 * Pure and side-effect free — never touches window / the store.
 */
export function readStoreAckState(storeMsg: AckSource | null | undefined): AckState {
  const m: AckSource = storeMsg ?? {};
  const ack = toAck(m.ack);

  const recipients = extractRecipientAcks(m);
  if (recipients) {
    return {
      ack,
      delivered: recipients.filter((a) => a >= ACK.DEVICE).length,
      read: recipients.filter((a) => a >= ACK.READ).length,
    };
  }

  const counts = extractCounts(m);
  if (counts) {
    return { ack, delivered: counts.delivered, read: counts.read };
  }

  // DM / lean row → derive from the scalar ack.
  return {
    ack,
    delivered: ack >= ACK.DEVICE ? 1 : 0,
    read: ack >= ACK.READ ? 1 : 0,
  };
}

/**
 * Normalize the local Redux doc's KNOWN ack into the same canonical shape. The
 * doc carries `ack` (scalar) plus an optional `ackData` in the "remaining"
 * representation the real-time handler persists
 * (`{ deliveryRemaining, readRemaining, playedRemaining, totalRecipients }`),
 * so delivered = total - deliveryRemaining, read = total - readRemaining.
 *
 * Defensive: no usable ackData → derive counts from the scalar ack.
 */
export function knownAckStateFromDoc(doc: { ack?: unknown; ackData?: unknown }): AckState {
  const ack = toAck(doc?.ack);
  const ad = doc?.ackData;
  if (ad && typeof ad === 'object') {
    const r = ad as Record<string, unknown>;
    const total = toNum(r.totalRecipients);
    if (total != null && total > 0) {
      const deliveryRemaining = toNum(r.deliveryRemaining) ?? total;
      const readRemaining = toNum(r.readRemaining) ?? total;
      return {
        ack,
        delivered: clamp(total - deliveryRemaining, 0, total),
        read: clamp(total - readRemaining, 0, total),
      };
    }
  }
  return {
    ack,
    delivered: ack >= ACK.DEVICE ? 1 : 0,
    read: ack >= ACK.READ ? 1 : 0,
  };
}

/**
 * Strictly-monotonic advance predicate: true iff the store observation is
 * further along than what we already know on AT LEAST one axis. Strict-greater
 * on every axis means a stale or equal store read can never fabricate an
 * advance, and the count-based representation means a group whose scalar ack is
 * pinned at SERVER can still report progress via the delivered/read axes.
 */
export function isMonotonicAdvance(store: AckState, known: AckState): boolean {
  return (
    store.ack > known.ack ||
    store.delivered > known.delivered ||
    store.read > known.read
  );
}
